// src/pages/income/env/disinfection/DisinfectionClientModal.jsx
// 환경/소독 청구서 발행 정보 관리 모달 — 거래처 자체는 income.Partner가 유일 기준.
// 여기서는 "Partner 선택(또는 신규 Partner 생성) + 발행 정보(금액/수신처/용역명
// 템플릿 등) 입력"만 다룬다. 기존 행의 Partner 연결은 바꾸지 않음(발행 정보만 수정).
import React, { useEffect, useState } from "react";
import api from "../../../../api";
import styles from "./DisinfectionInvoicePage.module.css";

const BLANK_NEW = {
  partnerId: null,
  partnerName: "",
  service_name_template: "{name} {year}년 {month}월 소독(방역)",
  amount: 0,
  recipient: "",
  sort_order: 0,
};

export default function DisinfectionClientModal({ onClose, onChanged }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [adding, setAdding] = useState(false);

  const [newClient, setNewClient] = useState(BLANK_NEW);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [showPartnerList, setShowPartnerList] = useState(false);
  const [searchingPartner, setSearchingPartner] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("disinfection/clients/");
      setClients(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Partner 검색(부분일치) — 새 발행 거래처 추가 시 기존 Partner를 찾기 위함
  useEffect(() => {
    let ignore = false;
    const q = newClient.partnerName.trim();
    if (!q || newClient.partnerId) {
      setPartnerOptions([]);
      return;
    }
    setSearchingPartner(true);
    const t = setTimeout(async () => {
      try {
        const res = await api.get("partners/", { params: { q } });
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        if (!ignore) setPartnerOptions(data);
      } finally {
        if (!ignore) setSearchingPartner(false);
      }
    }, 250);
    return () => { ignore = true; clearTimeout(t); };
  }, [newClient.partnerName, newClient.partnerId]);

  const updateClient = (id, patch) => {
    setClients((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const saveClient = async (client) => {
    setSavingId(client.id);
    try {
      // partner는 이 화면에서 변경하지 않음 — 발행 정보 필드만 전송
      const { id, partner_name, partner, ...payload } = client;
      await api.patch(`disinfection/clients/${client.id}/`, payload);
      await load();
      onChanged?.();
    } catch (e) {
      alert("저장 실패: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingId(null);
    }
  };

  const deactivate = async (client) => {
    if (!window.confirm(`[${client.partner_name}] 발행 정보를 비활성화하시겠습니까?`)) return;
    setSavingId(client.id);
    try {
      await api.delete(`disinfection/clients/${client.id}/`);
      await load();
      onChanged?.();
    } finally {
      setSavingId(null);
    }
  };

  const reactivate = async (client) => {
    setSavingId(client.id);
    try {
      await api.patch(`disinfection/clients/${client.id}/`, { is_active: true });
      await load();
      onChanged?.();
    } finally {
      setSavingId(null);
    }
  };

  const pickPartner = (p) => {
    setNewClient((prev) => ({ ...prev, partnerId: p.id, partnerName: p.name }));
    setShowPartnerList(false);
  };

  const createAndPickPartner = async () => {
    const name = newClient.partnerName.trim();
    if (!name) return;
    try {
      const res = await api.post("partners/", { name });
      pickPartner(res.data);
    } catch (e) {
      alert("신규 거래처 등록 실패: " + (e?.response?.data?.detail || e.message));
    }
  };

  const addClient = async () => {
    if (!newClient.partnerId) {
      alert("거래처(Partner)를 목록에서 선택하거나 신규 등록하세요.");
      return;
    }
    setAdding(true);
    try {
      await api.post("disinfection/clients/", {
        partner: newClient.partnerId,
        service_name_template: newClient.service_name_template,
        amount: newClient.amount,
        recipient: newClient.recipient,
        sort_order: newClient.sort_order,
      });
      setNewClient(BLANK_NEW);
      await load();
      onChanged?.();
    } catch (e) {
      alert("추가 실패: " + (e?.response?.data?.detail || e.message));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>청구서 발행 거래처 관리</h3>
          <button className={styles.closeBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.addRow} style={{ position: "relative" }}>
            <div style={{ position: "relative" }}>
              <input
                className={`${styles.smallInput} ${styles.wideInput}`}
                placeholder="거래처 검색(Partner) 또는 신규 입력"
                value={newClient.partnerName}
                onChange={(e) => {
                  setNewClient((p) => ({ ...p, partnerName: e.target.value, partnerId: null }));
                  setShowPartnerList(true);
                }}
                onFocus={() => setShowPartnerList(true)}
              />
              {showPartnerList && newClient.partnerName.trim() && !newClient.partnerId && (
                <ul style={{
                  position: "absolute", zIndex: 999, background: "#fff", top: "34px", left: 0,
                  border: "1px solid #d1d5db", borderRadius: "6px", listStyle: "none",
                  margin: 0, padding: "4px 0", width: "260px", maxHeight: "220px", overflowY: "auto",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}>
                  {searchingPartner ? (
                    <li style={{ padding: "6px 12px", color: "#6b7280", fontSize: "13px" }}>검색 중...</li>
                  ) : partnerOptions.length > 0 ? (
                    partnerOptions.map((p) => (
                      <li key={p.id}
                        style={{ padding: "6px 12px", cursor: "pointer", fontSize: "13px" }}
                        onClick={() => pickPartner(p)}
                      >{p.name}</li>
                    ))
                  ) : (
                    <li
                      style={{ padding: "6px 12px", cursor: "pointer", fontSize: "13px", color: "#2563eb" }}
                      onClick={createAndPickPartner}
                    >
                      + 신규 거래처로 등록: "{newClient.partnerName.trim()}"
                    </li>
                  )}
                </ul>
              )}
            </div>
            <input
              className={`${styles.smallInput} ${styles.wideInput}`}
              placeholder="용역명 템플릿"
              value={newClient.service_name_template}
              onChange={(e) => setNewClient((p) => ({ ...p, service_name_template: e.target.value }))}
            />
            <input
              className={styles.smallInput}
              type="number"
              placeholder="금액"
              value={newClient.amount}
              onChange={(e) => setNewClient((p) => ({ ...p, amount: Number(e.target.value) }))}
            />
            <input
              className={styles.smallInput}
              placeholder="수신처"
              value={newClient.recipient}
              onChange={(e) => setNewClient((p) => ({ ...p, recipient: e.target.value }))}
            />
            <input
              className={styles.smallInput}
              type="number"
              placeholder="정렬순서"
              value={newClient.sort_order}
              onChange={(e) => setNewClient((p) => ({ ...p, sort_order: Number(e.target.value) }))}
            />
            <button className={styles.addBtn} onClick={addClient} disabled={adding}>
              {adding ? "추가중..." : "+ 발행 거래처 추가"}
            </button>
          </div>

          <table className={styles.table}>
            <thead>
              <tr>
                <th>거래처(Partner)</th>
                <th>용역명 템플릿</th>
                <th>금액</th>
                <th>수신처</th>
                <th>정렬순서</th>
                <th className={styles.actionCell}>저장</th>
                <th className={styles.actionCell}>사용여부</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className={styles.emptyCell}>불러오는 중...</td></tr>
              ) : clients.length === 0 ? (
                <tr><td colSpan={7} className={styles.emptyCell}>등록된 발행 거래처가 없습니다.</td></tr>
              ) : (
                clients.map((c) => (
                  <tr key={c.id} className={!c.is_active ? styles.inactiveRow : undefined}>
                    <td className={styles.nowrapCell}>{c.partner_name}</td>
                    <td>
                      <input
                        className={`${styles.smallInput} ${styles.wideInput}`}
                        value={c.service_name_template}
                        onChange={(e) => updateClient(c.id, { service_name_template: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className={styles.smallInput}
                        type="number"
                        value={c.amount}
                        onChange={(e) => updateClient(c.id, { amount: Number(e.target.value) })}
                      />
                    </td>
                    <td>
                      <input
                        className={styles.smallInput}
                        value={c.recipient}
                        onChange={(e) => updateClient(c.id, { recipient: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className={styles.smallInput}
                        type="number"
                        value={c.sort_order}
                        onChange={(e) => updateClient(c.id, { sort_order: Number(e.target.value) })}
                      />
                    </td>
                    <td className={styles.actionCell}>
                      <button
                        className={styles.saveBtn}
                        onClick={() => saveClient(c)}
                        disabled={savingId === c.id}
                      >
                        {savingId === c.id ? "저장중" : "저장"}
                      </button>
                    </td>
                    <td className={styles.actionCell}>
                      {c.is_active ? (
                        <button
                          className={styles.deactivateBtn}
                          onClick={() => deactivate(c)}
                          disabled={savingId === c.id}
                        >
                          비활성화
                        </button>
                      ) : (
                        <button
                          className={styles.saveBtn}
                          onClick={() => reactivate(c)}
                          disabled={savingId === c.id}
                        >
                          재활성화
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.manageBtn} onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
